import { Link } from 'react-router-dom';

export function Landing() {
  return (
    <div className="min-h-screen bg-white text-ink-900 flex flex-col">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-ink-100">
        <Wordmark />
        <nav className="flex items-center gap-6 text-sm">
          <a href="#features" className="text-ink-600 hover:text-ink-900 transition">Features</a>
          <a href="#integrity" className="text-ink-600 hover:text-ink-900 transition">Integrity</a>
          <Link to="/verify" className="text-ink-600 hover:text-ink-900 transition">Verify</Link>
          <Link to="/login" className="text-ink-600 hover:text-ink-900 transition">Sign in</Link>
          <Link
            to="/signup"
            className="inline-flex items-center px-4 py-2 bg-ink-900 text-white text-sm font-medium rounded-lg hover:bg-ink-800 transition shadow-soft"
          >
            Start free
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-50/40 via-white to-white pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-50 text-brand-700 text-xs font-medium rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              Built for the Louisiana Planned Community Act
            </div>
            <h1 className="text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.05]">
              HOA voting that holds up <span className="text-ink-500">in court.</span>
            </h1>
            <p className="mt-6 text-lg text-ink-600 max-w-xl leading-relaxed">
              Run board elections, bylaw amendments, and special-assessment votes with a cryptographic audit trail, tamper-evident hash chain, and signed PDF reports.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-6 py-3 bg-ink-900 text-white font-medium rounded-lg hover:bg-ink-800 transition shadow-soft"
              >
                Start your HOA
              </Link>
              <a
                href="#integrity"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-ink-900 font-medium rounded-lg border border-ink-200 hover:border-ink-300 hover:bg-ink-50 transition"
              >
                How it works
              </a>
            </div>
            <p className="mt-5 text-sm text-ink-500">
              7-day notice, quorum and threshold rules enforced out of the box. La. R.S. 9:1141 <em>et seq.</em>
            </p>
          </div>

          {/* Audit-chain code preview */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-brand-100 to-transparent rounded-2xl blur-xl opacity-60" />
              <div className="relative bg-ink-900 rounded-xl shadow-card overflow-hidden border border-ink-800">
                <div className="flex items-center gap-1.5 px-4 py-3 border-b border-ink-800">
                  <span className="w-2.5 h-2.5 rounded-full bg-ink-700" />
                  <span className="w-2.5 h-2.5 rounded-full bg-ink-700" />
                  <span className="w-2.5 h-2.5 rounded-full bg-ink-700" />
                  <span className="ml-auto text-xs text-ink-500 font-mono">audit_log entry</span>
                </div>
                <pre className="px-5 py-4 text-[12.5px] font-mono text-ink-200 leading-relaxed">
{`event:    `}<span className="text-brand-400">ballot.cast</span>{`
sequence: 247
prev:     `}<span className="text-ink-500">a4f3...91cb</span>{`
hash:     `}<span className="text-ink-500">b7e2...d401</span>{`
payload:  { voteId, residentId, lotId, contentHash }
signed:   `}<span className="text-brand-400">ed25519</span>{` by key 7c4e...9a13`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-ink-50 border-y border-ink-100 py-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="max-w-2xl">
            <div className="text-sm font-medium text-brand-600 uppercase tracking-wider">Capabilities</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Everything an HOA actually needs to run a defensible vote.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Feature title="4 ballot types" body="Yes/No/Abstain, single-choice, multi-choice (pick N of M), and ranked-choice (instant runoff)." />
            <Feature title="Per-tenant thresholds" body="Quorum and passing thresholds default to LA statutory floor; override with a citation from your governing documents." />
            <Feature title="Tamper-evident audit" body="Every event hashed into an append-only chain. Worker-signed PDF reports prove the record wasn't altered." />
            <Feature title="Co-ownership math" body="Lots with multiple owners resolved per LA RS 9:1141.28(E) majority-in-interest rule." />
            <Feature title="Pluggable email" body="Use SMTP2Go or SendGrid; swap providers from settings without redeploying." />
            <Feature title="Public verification" body="Anyone can independently verify your audit chain at /verify — they don't have to trust us." />
          </div>
        </div>
      </section>

      {/* Integrity */}
      <section id="integrity" className="py-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <div className="text-sm font-medium text-brand-600 uppercase tracking-wider">Security model</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">The integrity story is the product.</h2>
          <ul className="mt-10 space-y-5">
            {INTEGRITY_POINTS.map((point) => (
              <li key={point.title} className="flex gap-4">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-50 text-brand-600 grid place-items-center mt-0.5">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <div>
                  <div className="font-medium text-ink-900">{point.title}</div>
                  <div className="text-ink-600 mt-1 leading-relaxed">{point.body}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-12 p-5 bg-ink-50 rounded-xl border border-ink-100 text-sm text-ink-700">
            Want to verify someone else's audit chain? Visit <Link to="/verify" className="text-brand-700 font-medium hover:underline">the public verifier</Link> and paste the chain root from any audit PDF.
          </div>
        </div>
      </section>

      <footer className="border-t border-ink-100 py-8 px-6 text-center text-sm text-ink-500">
        <Wordmark className="justify-center mb-3" />
        <p>
          &copy; {new Date().getFullYear()} HOA-Vote ·{' '}
          <Link to="/verify" className="hover:text-ink-700 transition">Verify any HOA's audit chain</Link>
        </p>
        <p className="mt-2 text-xs text-ink-400 max-w-xl mx-auto">
          Not legal advice — boards remain responsible for compliance with their declaration and applicable state law.
        </p>
      </footer>
    </div>
  );
}

function Wordmark({ className = '' }: { className?: string }) {
  return (
    <Link to="/" className={`inline-flex items-center gap-2 font-semibold text-lg tracking-tight text-ink-900 ${className}`}>
      <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="7" fill="#0f172a" />
        <path d="M9 16l5 5 9-11" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      HOA-Vote
    </Link>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-white border border-ink-100 rounded-xl p-6 hover:border-ink-200 hover:shadow-soft transition">
      <h3 className="font-semibold text-ink-900">{title}</h3>
      <p className="text-sm text-ink-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

const INTEGRITY_POINTS = [
  {
    title: 'Atomic ballot writes',
    body: 'One Durable Object per active election serializes every vote write — no race conditions, no double-votes, no two-tokens-cast-simultaneously edge cases.',
  },
  {
    title: 'Append-only hash chain',
    body: 'Every state change is hashed with the previous row’s hash. Mutating any row breaks the chain. The verifier catches it instantly.',
  },
  {
    title: 'Ed25519-signed receipts',
    body: 'Audit PDFs embed the chain root hash plus a Worker-signed signature. Voters can prove their receipt is genuine without trusting the operator.',
  },
  {
    title: 'No raw PII at rest',
    body: 'IPs and user agents are stored only as per-tenant-salted SHA-256 hashes. Magic-link tokens are HMAC-signed, single-use, never stored as plaintext.',
  },
  {
    title: 'Encrypted credentials',
    body: 'Email provider API keys are AES-GCM encrypted at rest with a key held in a separate trust boundary from the database.',
  },
];
